import { choraleJson, derivedChorale } from './chorales';
import { CHORALE_FILES } from './chorales/catalog.generated';
import type { PieceData } from './types';
import type { HarmonyEvent } from '../music/piece';
import { mod, pcNumber } from '../music/pitch';
import { buildView, PieceView } from '../ui/model';

/**
 * Tune rooms: every tune Bach harmonized more than once, with the settings lined up note by note
 * under one melody. Settings differ in key, metre and rhythm, and Bach sometimes ornaments the
 * melody, so they are aligned by the melody's notes, not by time: a dynamic-programming alignment
 * of pitch classes (after the best transposition), with a bonus when fermatas meet. Deterministic.
 */

export interface MelodyNote {
  onset: number;
  dur: number;
  midi: number;
  fermata?: boolean;
  lyric?: string;
}

export interface Alignment {
  /** For each reference note, the index of the aligned note in the other melody (or null). */
  map: Array<number | null>;
  /** Semitones to add to the other melody so it sounds at the reference's pitch. */
  shift: number;
  /** Reference notes aligned to a note of the same pitch class. */
  matched: number;
}

export const MIN_AGREEMENT = 0.7;

const fold = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/ß/g, 'ss');
export function slugOf(tune: string): string {
  return fold(tune)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

/** Tidy a tune name from the source metadata (a stray bracket, stray spaces). */
export function cleanName(name: string): string {
  let n = name.replace(/\s+/g, ' ').trim();
  if (n.includes(']') && !n.includes('[')) n = n.replace(/\]/g, '');
  return n;
}

export interface TuneGroup {
  slug: string;
  name: string;
  ids: string[];
}

let groups: TuneGroup[] | null = null;
/** Tunes with at least two settings in the atlas (derived duplicates left out), most settings first. */
export function tuneGroups(): TuneGroup[] {
  if (!groups) {
    const map = new Map<string, string[]>();
    // From the stored chorales, not the corpus index (which is built from these rooms).
    for (const f of CHORALE_FILES) {
      if (!f.tune) continue;
      map.set(f.tune, [...(map.get(f.tune) ?? []), f.id]);
    }
    groups = [...map.entries()]
      .filter(([, ids]) => ids.length >= 2)
      .map(([name, ids]) => ({ slug: slugOf(name), name: cleanName(name), ids }))
      .sort((a, b) => b.ids.length - a.ids.length || a.name.localeCompare(b.name));
  }
  return groups;
}

export function melodyOf(id: string): MelodyNote[] {
  const c = choraleJson(id);
  if (!c) return [];
  return c.notes
    .filter((n) => n.line === 3)
    .sort((a, b) => a.onset - b.onset || b.midi - a.midi)
    .filter((n, i, all) => i === 0 || Math.abs(all[i - 1].onset - n.onset) > 1e-6) // top note of a divisi
    .map((n) => ({ onset: n.onset, dur: n.dur, midi: n.midi, fermata: n.fermata, lyric: n.lyric }));
}

const MATCH = 3;
const FERMATA_BONUS = 1;
const SUBSTITUTE = -2;
const SKIP_REF = -2; // a reference note with no counterpart
const SKIP_OTHER = -1; // an extra note in the other melody (usually an ornament)

function alignWithShift(ref: MelodyNote[], other: MelodyNote[], shift: number): { map: Array<number | null>; score: number; matched: number } {
  const n = ref.length;
  const m = other.length;
  const W = m + 1;
  const score = new Float64Array((n + 1) * W);
  const move = new Uint8Array((n + 1) * W); // 1 diagonal, 2 up (skip ref), 3 left (skip other)
  for (let i = 1; i <= n; i++) {
    score[i * W] = i * SKIP_REF;
    move[i * W] = 2;
  }
  for (let j = 1; j <= m; j++) {
    score[j] = j * SKIP_OTHER;
    move[j] = 3;
  }
  for (let i = 1; i <= n; i++) {
    const a = ref[i - 1];
    const pa = mod(a.midi, 12);
    for (let j = 1; j <= m; j++) {
      const b = other[j - 1];
      const same = pa === mod(b.midi + shift, 12);
      const diag = score[(i - 1) * W + j - 1] + (same ? MATCH + (a.fermata && b.fermata ? FERMATA_BONUS : 0) : SUBSTITUTE);
      const up = score[(i - 1) * W + j] + SKIP_REF;
      const left = score[i * W + j - 1] + SKIP_OTHER;
      let best = diag;
      let mv = 1;
      if (up > best) {
        best = up;
        mv = 2;
      }
      if (left > best) {
        best = left;
        mv = 3;
      }
      score[i * W + j] = best;
      move[i * W + j] = mv;
    }
  }
  const map: Array<number | null> = new Array(n).fill(null);
  let matched = 0;
  let i = n;
  let j = m;
  while (i > 0 || j > 0) {
    const mv = move[i * W + j];
    if (mv === 1) {
      map[i - 1] = j - 1;
      if (mod(ref[i - 1].midi, 12) === mod(other[j - 1].midi + shift, 12)) matched++;
      i--;
      j--;
    } else if (mv === 2) i--;
    else j--;
  }
  return { map, score: score[n * W + m], matched };
}

/** Align another setting's melody to the reference melody. */
export function alignMelodies(ref: MelodyNote[], other: MelodyNote[]): Alignment {
  if (!ref.length || !other.length) return { map: ref.map(() => null), shift: 0, matched: 0 };
  // Candidate transpositions: the finals agree, the first notes agree, or the pitch-class profiles agree best.
  const profile = (mel: MelodyNote[]) => {
    const h = new Array(12).fill(0);
    for (const x of mel) h[mod(x.midi, 12)] += x.dur;
    return h;
  };
  const pr = profile(ref);
  const po = profile(other);
  let bestProfile = 0;
  let bestDot = -1;
  for (let s = 0; s < 12; s++) {
    let dot = 0;
    for (let k = 0; k < 12; k++) dot += pr[mod(k + s, 12)] * po[k];
    if (dot > bestDot) {
      bestDot = dot;
      bestProfile = s;
    }
  }
  const candidates = [
    ...new Set([
      mod(ref[ref.length - 1].midi - other[other.length - 1].midi, 12),
      mod(ref[0].midi - other[0].midi, 12),
      bestProfile,
    ]),
  ];
  let best: { map: Array<number | null>; score: number; matched: number; shift: number } | null = null;
  for (const s of candidates) {
    const r = alignWithShift(ref, other, s);
    if (!best || r.score > best.score || (r.score === best.score && s < best.shift)) best = { ...r, shift: s };
  }
  // Exact shift in semitones (octave included): the median difference over matched notes.
  const diffs: number[] = [];
  best!.map.forEach((j, i) => {
    if (j !== null && mod(ref[i].midi - other[j].midi - best!.shift, 12) === 0) diffs.push(ref[i].midi - other[j].midi);
  });
  diffs.sort((a, b) => a - b);
  const shift = diffs.length ? diffs[Math.floor(diffs.length / 2)] : best!.shift > 6 ? best!.shift - 12 : best!.shift;
  return { map: best!.map, shift, matched: best!.matched };
}

/* ---------- Rooms ---------- */

export interface RoomCell {
  col: number;
  /** Index of the aligned note in this setting's melody, or null where the setting has none. */
  note: number | null;
  onset: number;
  dur: number;
  /** The melody note heard here, moved to the reference pitch. */
  midi: number | null;
  same: boolean;
  event: HarmonyEvent | null;
  tension: number;
  /** Chord identity heard against the reference pitch: root pitch class and quality ("7:major"). */
  chord: string | null;
  lyric?: string;
}

export interface RoomSetting {
  id: string;
  piece: PieceData;
  view: PieceView;
  melody: MelodyNote[];
  shift: number;
  agreement: number;
  cells: RoomCell[];
}

export interface TuneRoom {
  slug: string;
  name: string;
  reference: RoomSetting;
  /** Settings that follow the reference melody closely enough to compare (reference first). */
  settings: RoomSetting[];
  /** Settings of a different version of the tune, listed but not lined up. */
  variants: RoomSetting[];
  columns: number;
  /** [first column, last column] of each line of the reference. */
  lines: Array<[number, number]>;
  /** Per column: how many different chords the settings put under this note. */
  distinct: number[];
  /** 0 when every setting harmonizes every note alike, 1 when every setting differs everywhere. */
  variety: number;
}

function eventAt(view: PieceView, t: number): number {
  const ev = view.events;
  for (let i = ev.length - 1; i >= 0; i--) if (ev[i].start <= t + 1e-6) return i;
  return 0;
}

function settingOf(id: string, ref: MelodyNote[] | null): RoomSetting | null {
  const d = derivedChorale(id);
  if (!d) return null;
  const melody = melodyOf(id);
  const view = buildView(d.piece);
  const al = ref ? alignMelodies(ref, melody) : { map: melody.map((_, i) => i), shift: 0, matched: melody.length };
  const cells: RoomCell[] = al.map.map((j, col) => {
    if (j === null) return { col, note: null, onset: 0, dur: 0, midi: null, same: false, event: null, tension: 0, chord: null };
    const n = melody[j];
    const i = eventAt(view, n.onset);
    const ev = view.events[i];
    const root = mod(pcNumber(ev.chord.root) + al.shift, 12);
    return {
      col,
      note: j,
      onset: n.onset,
      dur: n.dur,
      midi: n.midi + al.shift,
      same: ref ? mod(n.midi + al.shift, 12) === mod(ref[col].midi, 12) : true,
      event: ev,
      tension: view.tension.perEvent[i].value,
      chord: `${root}:${ev.chord.triad}`,
      lyric: n.lyric,
    };
  });
  return { id, piece: d.piece, view, melody, shift: al.shift, agreement: ref ? al.matched / ref.length : 1, cells };
}

const rooms = new Map<string, TuneRoom | null>();
export function tuneRoom(slug: string): TuneRoom | null {
  if (rooms.has(slug)) return rooms.get(slug)!;
  const g = tuneGroups().find((x) => x.slug === slug);
  let room: TuneRoom | null = null;
  if (g) {
    // Reference: the setting whose melody the others follow best (then the plainest, then catalogue order).
    const melodies = new Map(g.ids.map((id) => [id, melodyOf(id)]));
    let refId = g.ids[0];
    let bestAvg = -1;
    for (const id of g.ids) {
      const r = melodies.get(id)!;
      if (!r.length) continue;
      const others = g.ids.filter((x) => x !== id);
      const avg = others.reduce((a, x) => a + alignMelodies(r, melodies.get(x)!).matched / r.length, 0) / others.length;
      const better = avg > bestAvg + 1e-9 || (Math.abs(avg - bestAvg) < 1e-9 && r.length < melodies.get(refId)!.length);
      if (better) {
        bestAvg = avg;
        refId = id;
      }
    }
    const refMel = melodies.get(refId)!;
    const reference = settingOf(refId, null)!;
    const all = g.ids.filter((id) => id !== refId).map((id) => settingOf(id, refMel)!);
    const settings = [reference, ...all.filter((s) => s.agreement >= MIN_AGREEMENT)];
    const variants = all.filter((s) => s.agreement < MIN_AGREEMENT);
    const columns = refMel.length;
    const phrases = derivedChorale(refId)!.piece.moments.map((m) => [m.start ?? 0, m.end ?? 0] as [number, number]);
    const lines: Array<[number, number]> = [];
    for (const [a, b] of phrases) {
      const cols = refMel.map((n, i) => (n.onset >= a - 1e-6 && n.onset < b - 1e-6 ? i : -1)).filter((i) => i >= 0);
      if (cols.length) lines.push([cols[0], cols[cols.length - 1]]);
    }
    const distinct = Array.from({ length: columns }, (_, c) => new Set(settings.map((s) => s.cells[c].chord).filter(Boolean)).size);
    const variety = settings.length > 1 ? distinct.reduce((a, d) => a + (d - 1) / (settings.length - 1), 0) / columns : 0;
    room = { slug: g.slug, name: g.name, reference, settings, variants, columns, lines, distinct, variety };
  }
  rooms.set(slug, room);
  return room;
}

/** The tune room a chorale belongs to, if its tune has other settings. */
export function tuneOf(pieceId: string): TuneGroup | undefined {
  return tuneGroups().find((g) => g.ids.includes(pieceId));
}
